import {inject, Injectable} from '@angular/core';
import {Actions, concatLatestFrom, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  DatevIncometaxExportConfigService,
  FullExportConfigDataDTO,
  SelectiveExportConfigDataDTO,
  UpdateExportConfigRequestDTO
} from '../../../../../openapi/export-openapi';
import {DatevWizardConfigDetailsActions} from './datev-wizard-config-details.actions';
import {DatevWizardGetFirmaSelectors} from '../../datev-wizard-get-firma/+store/datev-wizard-get-firma.selectors';
import {DatevWizardClientIdSelectors} from '../../datev-wizard-client-id/+store/datev-wizard-client-id.selectors';
import {DatevWizardClientIdActions} from '../../datev-wizard-client-id/+store/datev-wizard-client-id.actions';
import {catchError, combineLatest, distinctUntilChanged, filter, map, of, switchMap, tap} from 'rxjs';
import {mappedHttpErrorResponseOperator, RouterActions} from '@adnova/jf-ng-components';
import {
  DialogDeleteConfigurationActions
} from '../../../../dialogs/dialog-delete-configuration/+store/dialog-delete-configuration.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {formatDateToYYYYMMDD} from '../../../../../utils/date.utils';
import {DEFAULT_EXPORT_CONFIG_NAME} from '../../+store/datev-wizard.constants';
import {DatevWizardExportDateActions} from '../../datev-wizard-export-date/+store/datev-wizard-export-date.actions';
import {DatevWizardCaseSelectionActions} from '../../datev-wizard-case-selection/+store/datev-wizard-case-selection.actions';
import {DatevWizardCaseSelectionSelectors} from '../../datev-wizard-case-selection/+store/datev-wizard-case-selection.selectors';
import {ErrorEventEntitiesEffects} from '../../../../../store/error-event-entities/error-event-entities.effects';
import {ErrorEventEntitiesActions} from '../../../../../store/error-event-entities/error-event-entities.actions';
import {DatevWizardConfigDetailsSelectors} from './datev-wizard-config-details.selectors';
import {DatevWizardGetFirmaActions} from '../../datev-wizard-get-firma/+store/datev-wizard-get-firma.actions';
import {DatevWizardRetryContextService} from '../../datev-wizard-login/services/datev-wizard-retry-context.service';


@Injectable()
export class DatevWizardConfigDetailsEffects {

  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _exportConfigService = inject(DatevIncometaxExportConfigService);
  private readonly _errorEventEntitiesEffects = inject(ErrorEventEntitiesEffects);
  private readonly _retryContextService = inject(DatevWizardRetryContextService);

  /**
   * Lädt Error Events für eine Export-Konfiguration.
   *
   * @trigger loadErrorEvents
   * @dispatch loadErrorEventsSuccess | loadErrorEventsFailure
   * @sideEffect API-Call zu /api/export-config/{id}/jobs
   */
  readonly loadErrorEvents$ = this._errorEventEntitiesEffects.loadErrorEvents$(
    ErrorEventEntitiesActions.loadErrorEvents,
  );

  /**
   * Lädt Jobs automatisch wenn sich relevante Dependencies ändern.
   * Reagiert auf Änderungen von:
   * - exportConfigId (aus existingExportConfig)
   * - errorEventsPage.pageable.number (Seitennummer)
   * - errorEventsPage.pageable.size (Seitengröße)
   *
   * @trigger Änderung einer der Abhängigkeiten
   * @dispatch ErrorEventEntitiesActions.loadErrorEvents
   * @description Zentraler Effect für Job-Loading - ersetzt alle anderen Initialisierungen
   */
  readonly loadJobsWhenDependenciesChange$ = createEffect(
    () => combineLatest([
      this._store.select(DatevWizardClientIdSelectors.existingExportConfigId),
      this._store.select(DatevWizardConfigDetailsSelectors.errorEventsPage),
    ]).pipe(
      distinctUntilChanged((prev, curr) =>
        prev[0] === curr[0] &&
        prev[1]?.pageable?.number === curr[1]?.pageable?.number &&
        prev[1]?.pageable?.size === curr[1]?.pageable?.size
      ),
      filter(([exportConfigId]) => !!exportConfigId),
      map(([exportConfigId, page]) => {
        const pageIndex = page?.pageable?.number ?? 0;
        const pageSize = page?.pageable?.size ?? 10;
        return ErrorEventEntitiesActions.loadErrorEvents({
          exportConfigId: exportConfigId!,
          pageIndex,
          pageSize,
        });
      })
    )
  );

  /**
   * Effect zum Synchronisieren der Firma aus der Route nach erfolgreichem Config-Laden.
   * Wird nur für config-details (redirectWhenFinished=false) ausgelöst.
   *
   * @trigger DatevWizardClientIdActions.getExistingExportConfigSuccess
   * @dispatch DatevWizardGetFirmaActions.syncFirmaFromRoute
   * @description Extrahiert die Firma-ID aus der geladenen Export-Config und dispatcht syncFirmaFromRoute.
   * Nutzt syncFirmaFromRoute statt setFirma um Race Conditions mit OIDC-Auth und Navigation zu vermeiden.
   */
  readonly setFirmaAfterConfigLoad$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.getExistingExportConfigSuccess),
      filter(({ redirectWhenFinished }) => !redirectWhenFinished),
      map(({ existingExportConfig }) => {
        const firmaId = existingExportConfig.firma?.id;
        if (firmaId) {
          return DatevWizardGetFirmaActions.syncFirmaFromRoute({ firmenId: firmaId });
        }
        // Falls keine Firma-ID vorhanden, keine Action dispatchen
        return { type: 'NO_ACTION' };
      }),
      filter(action => action.type !== 'NO_ACTION')
    )
  );

  /**
   * Effect zum Laden des Status, nachdem die Export-Config erfolgreich geladen wurde.
   * Wird nur getriggert wenn redirectWhenFinished=false (also auf config-details Seite).
   *
   * @trigger DatevWizardClientIdActions.getExistingExportConfigSuccess
   * @dispatch DatevWizardClientIdActions.getExistingExportConfigStatus
   * @description Lädt den Status der Konfiguration automatisch nach erfolgreichem Config-Laden
   */
  readonly loadStatusAfterConfigSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardClientIdActions.getExistingExportConfigSuccess),
      filter(({ redirectWhenFinished }) => !redirectWhenFinished),
      map(({ existingExportConfig }) => {
        return DatevWizardClientIdActions.getExistingExportConfigStatus({
          exportConfigId: existingExportConfig.id,
          redirectWhenFinished: false
        });
      })
    )
  );

  /**
   * Effect für das Erneuern der DATEV-Verbindung (Reconnect).
   * Funktioniert nur für bestehende inaktive Konfigurationen.
   */
  readonly connectToDatev$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.connectToDatev),
      concatLatestFrom(() => [
        this._store.select(DatevWizardClientIdSelectors.existingExportConfig),
        this._store.select(DatevWizardClientIdSelectors.existingExportConfigStatus),
      ]),
      // Prüfe ob eine bestehende Config vorhanden ist
      filter(([, existingConfig, existingStatus]) => {
        // Reconnect nur für bestehende inaktive Configs
        if (!existingConfig || !existingConfig.id) {
          this._snackBar.open('Fehler: Keine Export-Konfiguration gefunden', undefined, {
            duration: 5000,
            panelClass: 'error',
          });
          return false;
        }

        // Prüfe ob Verbindung bereits aktiv ist
        if (existingStatus?.connection === true) {
          this._snackBar.open('DATEV-Verbindung ist bereits aktiv', undefined, {
            duration: 3000,
            panelClass: 'info',
          });
          return false;
        }

        return true;
      }),
      switchMap(([, existingConfig]) => {
        // Reconnect für bestehende inaktive Config
        return this._exportConfigService.reconnectExportConfig(existingConfig!.id).pipe(
          map(response => DatevWizardConfigDetailsActions.connectToDatevSuccess({
            loginUrl: response.loginUrl,
          })),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(error => [
              DatevWizardConfigDetailsActions.connectToDatevFailure({error})
            ])
          ))
        );
      })
    )
  );

  /**
   * Effect für erfolgreiche Verbindung - navigiert zur DATEV Login-URL.
   * Setzt die redirect_uri auf die Login-Route mit firmenId und clientId als Query-Parameter.
   */
  readonly connectToDatevSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.connectToDatevSuccess),
      tap(({loginUrl}) => {
        // Speichere aktuelle URL für Retry bei Fehler
        this._retryContextService.saveRetryUrl(window.location.href);

        // Parse die Login-URL
        const url = new URL(loginUrl);

        // Navigiere zur DATEV Login-URL
        window.location.href = url.toString();
      })
    ),
    {dispatch: false}
  );

  /**
   * Effect für Verbindungsfehler - zeigt Fehlermeldung an.
   */
  readonly connectToDatevFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.connectToDatevFailure),
      tap(({error}) => {
        if (error.status === 409) {
          this._snackBar.open(
            'Eine Konfiguration für diesen Client existiert bereits',
            undefined,
            {
              duration: 5000,
              panelClass: 'error',
            }
          );
        } else {
          this._snackBar.open(
            'Fehler beim Verbinden mit DATEV',
            undefined,
            {
              duration: 5000,
              panelClass: 'error',
            }
          );
        }
      })
    ),
    {dispatch: false}
  );

  /**
   * Effect für das Trennen der DATEV-Verbindung.
   * Ruft die API auf um die Verbindung zu trennen.
   */
  readonly disconnectFromDatev$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.disconnectFromDatev),
      concatLatestFrom(() => [
        this._store.select(DatevWizardClientIdSelectors.existingExportConfig),
      ]),
      filter(([, exportConfig]) => {
        if (!exportConfig?.id) {
          this._snackBar.open('Fehler: Keine Export-Konfiguration gefunden', undefined, {
            duration: 5000,
            panelClass: 'error',
          });
          return false;
        }
        return true;
      }),
      switchMap(([, exportConfig]) => {
        // Nach dem Filter ist garantiert, dass exportConfig und exportConfig.id existieren
        if (!exportConfig?.id) {
          // Dieser Fall sollte nie eintreten wegen des Filters oben
          return of(DatevWizardConfigDetailsActions.disconnectFromDatevFailure({
            error: {status: 0, message: 'Keine Export-Konfiguration gefunden'}
          }));
        }
        return this._exportConfigService.postExportConfigDisconnect(exportConfig.id).pipe(
          map(() => DatevWizardConfigDetailsActions.disconnectFromDatevSuccess()),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(error => [
              DatevWizardConfigDetailsActions.disconnectFromDatevFailure({error})
            ])
          ))
        );
      })
    )
  );

  /**
   * Effect für erfolgreiche Trennung der Verbindung.
   * Zeigt Erfolgsmeldung an und aktualisiert den Status im Store.
   */
  readonly disconnectFromDatevSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.disconnectFromDatevSuccess),
      tap(() => {
        this._snackBar.open(
          'DATEV-Verbindung wurde getrennt',
          undefined,
          {
            duration: 3000,
            panelClass: 'success',
          }
        );
      }),
      map(() => DatevWizardClientIdActions.updateExportConfigStatusAfterDisconnect())
    )
  );

  /**
   * Effect für Fehler beim Trennen der Verbindung.
   * Zeigt Fehlermeldung an.
   */
  readonly disconnectFromDatevFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.disconnectFromDatevFailure),
      tap(({error}) => {
        this._snackBar.open(
          'Fehler beim Trennen der DATEV-Verbindung',
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );
      })
    ),
    {dispatch: false}
  );

  /**
   * Effect zum Löschen der Export-Konfiguration.
   * Ruft die API auf und handled Success/Failure.
   */
  readonly deleteConfiguration$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.deleteConfiguration),
      concatLatestFrom(() => [
        this._store.select(DatevWizardClientIdSelectors.existingExportConfig),
      ]),
      filter(([, exportConfig]) => {
        if (!exportConfig?.id) {
          this._snackBar.open('Fehler: Keine Export-Konfiguration zum Löschen gefunden', undefined, {
            duration: 5000,
            panelClass: 'error',
          });
          return false;
        }
        return true;
      }),
      switchMap(([, exportConfig]) => {
        // Nach dem Filter ist garantiert, dass exportConfig und exportConfig.id existieren
        if (!exportConfig?.id) {
          // Dieser Fall sollte nie eintreten wegen des Filters oben
          return of(DatevWizardConfigDetailsActions.deleteConfigurationFailure({
            error: {status: 0, message: 'Keine Export-Konfiguration gefunden'}
          }));
        }
        return this._exportConfigService.deleteExportConfig(exportConfig.id).pipe(
          map(() => DatevWizardConfigDetailsActions.deleteConfigurationSuccess()),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(mappedError => [
              DatevWizardConfigDetailsActions.deleteConfigurationFailure({error: mappedError})
            ])
          ))
        );
      })
    )
  );

  /**
   * Effect für erfolgreiche Löschung.
   * Schließt den Dialog, zeigt Erfolgsmeldung und dispatcht die Navigation-Action.
   */
  readonly deleteConfigurationSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.deleteConfigurationSuccess),
      concatLatestFrom(() => [
        this._store.select(DatevWizardGetFirmaSelectors.firmenId),
      ]),
      filter((data): data is [TypedAction<'[Datev Wizard Config Details] Delete Configuration Success'>, string] => {
        const [, firmenId] = data;
        if (!firmenId) {
          this._snackBar.open('Fehler: Keine Firmen-ID gefunden', undefined, {
            duration: 5000,
            panelClass: 'error',
          });
          return false;
        }
        return true;
      }),
      tap(() => {
        this._snackBar.open(
          'Export-Konfiguration wurde erfolgreich gelöscht',
          undefined,
          {
            duration: 3000,
            panelClass: 'success',
          }
        );
      }),
      switchMap(([, firmenId]) => [
        DialogDeleteConfigurationActions.close(),
        DatevWizardClientIdActions.resetExistingConfig(),
        DatevWizardCaseSelectionActions.resetCaseSelection(),
        // ConnectionName reset nicht mehr nötig - wird aus existingExportConfig gelesen
        DatevWizardExportDateActions.resetExportDate(),
        DatevWizardConfigDetailsActions.goToCaseSelection({firmenId})
      ])
    )
  );

  /**
   * Effect für Fehler beim Löschen.
   * Zeigt Fehlermeldung an.
   */
  readonly deleteConfigurationFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.deleteConfigurationFailure),
      tap(({error}) => {
        this._snackBar.open(
          'Fehler beim Löschen der Export-Konfiguration',
          undefined,
          {
            duration: 5000,
            panelClass: 'error',
          }
        );
      })
    ),
    {dispatch: false}
  );

  /**
   * Effect für Navigation zur Case-Selection Seite.
   * Navigiert zur Case-Selection Seite mit der übergebenen Firmen-ID (neuer Flow nach Löschung).
   */
  readonly goToCaseSelection$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.goToCaseSelection),
      map(({firmenId}) =>
        RouterActions.navigateByUrl({url: `/datev/income-tax-documents/wizard/case-selection/inhaber/${firmenId}`})
      )
    )
  );

  /**
   * Effect für Navigation zur Client-ID Seite.
   * Navigiert zur Client-ID Seite mit der übergebenen Firmen-ID.
   */
  readonly goToClientId$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.goToClientId),
      map(({firmenId}) =>
        RouterActions.navigateByUrl({url: `/datev/income-tax-documents/wizard/client-id/inhaber/${firmenId}`})
      )
    )
  );

  /**
   * Effect zum Aktualisieren der Export-Konfiguration.
   * Sendet die neuen Werte (ConnectionName + ggf. Export-Datum) an die API und aktualisiert bei Erfolg den Store.
   * Unterscheidet zwischen Fall 1 (FullExportConfigDataDTO) und Fall 2 (SelectiveExportConfigDataDTO).
   */
  readonly updateExportConfiguration$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.updateExportConfiguration),
      concatLatestFrom(() => [
        this._store.select(DatevWizardClientIdSelectors.existingExportConfig),
        this._store.select(DatevWizardCaseSelectionSelectors.selectedCase)
      ]),
      // Sicherheitsprüfung: Config muss existieren
      filter(([action, config, selectedCase]) => {
        if (!config?.id) {
          this._snackBar.open('Fehler: Keine Konfiguration zum Aktualisieren gefunden', undefined, {
            duration: 5000,
            panelClass: 'error'
          });
          return false;
        }
        return true;
      }),
      switchMap(([action, config, selectedCase]) => {
        // Erstelle Payload basierend auf selectedCase
        let payload: FullExportConfigDataDTO | SelectiveExportConfigDataDTO;

        if (selectedCase === 'fall1') {
          // Fall 1: Vollständiger Export mit Export-Datum
          payload = {
            type: 'FullExportConfigData',
            exportSinceDocumentDate: action.exportDateIndependent
              ? undefined
              : formatDateToYYYYMMDD(action.exportDate),
          } as FullExportConfigDataDTO;
        } else {
          // Fall 2: Selektiver Export ohne Export-Datum
          payload = {
            type: 'SelectiveExportConfigData',
          } as SelectiveExportConfigDataDTO;
        }

        const requestBody: UpdateExportConfigRequestDTO = {
          name: action.connectionName,
          payload: payload
        };

        // API-Call zum Aktualisieren der Konfiguration
        return this._exportConfigService.updateExportConfig(
          config!.id,
          requestBody
        ).pipe(
          map(() => DatevWizardConfigDetailsActions.updateExportConfigurationSuccess({
            connectionName: action.connectionName,
            exportDate: action.exportDate,
            exportDateIndependent: action.exportDateIndependent
          })),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            switchMap(mappedError => [
              DatevWizardConfigDetailsActions.updateExportConfigurationFailure({error: mappedError})
            ])
          ))
        );
      })
    )
  );

  /**
   * Effect bei erfolgreichem Update der Export-Konfiguration.
   * Zeigt eine Erfolgsmeldung an und aktualisiert den Store.
   */
  readonly updateExportConfigurationSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.updateExportConfigurationSuccess),
      concatLatestFrom(() => [
        this._store.select(DatevWizardClientIdSelectors.existingExportConfig)
      ]),
      tap(() => {
        this._snackBar.open('Export-Konfiguration wurde erfolgreich aktualisiert', undefined, {
          duration: 3000,
          panelClass: 'success'
        });
      }),
      switchMap(([{connectionName, exportDate, exportDateIndependent}, existingConfig]) => {
        const actions = [];

        // ConnectionName wird nicht mehr im Store gespeichert - wird direkt aus existingExportConfig gelesen

        // Export-Date Actions (nur bei Fall 1 relevant)
        if (exportDateIndependent) {
          actions.push(DatevWizardExportDateActions.setExportDateIndependent({exportDateIndependent}));
        } else if (exportDate) {
          actions.push(DatevWizardExportDateActions.setExportDate({exportDate}));
        }

        // ExistingExportConfig vom Backend neu laden
        if (existingConfig?.id) {
          actions.push(DatevWizardClientIdActions.getExistingExportConfig({
            exportConfigId: existingConfig.id,
            redirectWhenFinished: false
          }));
        }

        return actions;
      })
    )
  );

  /**
   * Effect bei fehlgeschlagenem Update der Export-Konfiguration.
   * Zeigt eine Fehlermeldung an.
   */
  readonly updateExportConfigurationFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardConfigDetailsActions.updateExportConfigurationFailure),
      tap(({error}) => {
        let message = 'Fehler beim Aktualisieren der Export-Konfiguration';

        // Spezifische Fehlermeldungen basierend auf Status-Code
        if (error.status === 404) {
          message = 'Die Export-Konfiguration wurde nicht gefunden';
        } else if (error.status === 403) {
          message = 'Sie haben keine Berechtigung, diese Konfiguration zu aktualisieren';
        } else if (error.status === 400) {
          message = 'Ungültige Daten. Bitte prüfen Sie Ihre Eingaben';
        }

        this._snackBar.open(message, undefined, {
          duration: 5000,
          panelClass: 'error'
        });
      })
    ),
    {dispatch: false}
  );

}
